import { HERRING, MARKET } from '../config.js';
import { readNumber, writeNumber } from './store.js';

// ---------------------------------------------------------------------------
// Fishing.
//
// Sail over a school, press space, and the boat lies to while the nets come
// up. Press space again to break off and keep whatever is aboard — the catch
// arrives fish by fish rather than as a lump at the end, so leaving early is a
// real choice and not a wasted minute.
//
// A school holds a fixed number of herring and takes its own time to work out,
// somewhere between ten and fifty seconds. Fish it dry and it disperses.
// ---------------------------------------------------------------------------

const STORE_KEY = 'sofatech:herring';
const PURSE_KEY = 'sofatech:guilders';

export class Fishing {
    constructor(schools, sailing, hud, fleet = null, market = null) {
        this.schools = schools;
        this.sailing = sailing;
        this.hud = hud;
        this.fleet = fleet;
        this.market = market;
        this.school = null;     // the one being worked, while fishing
        this.partial = 0;       // fish caught but not yet whole

        // The catch is kept per browser: a haul that vanished on reload would
        // make the counter feel like a toy. Nothing leaves the machine.
        // What is in the hold, and what has been banked. Selling is what
        // turns one into the other, which is the whole reason to sail home.
        this.hold = this._restore(STORE_KEY);
        this.guilders = this._restore(PURSE_KEY);
        // True for one frame when a fish comes aboard, so the card can kick.
        this.landed = false;
        this.sold = false;
    }

    _restore(key) {
        return readNumber(key);
    }

    _save() {
        writeNumber(STORE_KEY, this.hold);
        writeNumber(PURSE_KEY, this.guilders);
    }

    /** In the harbour the market stands on, with herring to sell? */
    get canSell() {
        if (this.hold <= 0) return false;
        // One hold: fish, people, or cargo.
        if (this.trading?.count > 0) return false;
        // A hold full of people is not a hold full of fish.
        if (this.crossing?.passengers > 0) return false;

        // The same idea of "alongside" the crossing uses: anywhere in
        // Volendam's basin counts, rather than one spot beside the jetty.
        if (this.crossing) return this.crossing.portAt()?.id === 'volendam';

        // No crossing built (tests, mostly): fall back to the jetty itself.
        if (!this.market?.mooring) return false;
        return this.market.mooring.distanceTo(this.sailing.position) < MARKET.radius;
    }

    /** What a herring fetches right now — the relay's price, or the default. */
    get price() {
        return this.fleet?.price ?? MARKET.price;
    }

    sell() {
        if (!this.canSell) return;
        const n = this.hold;
        this.guilders += Math.round(n * this.price);
        this.hold = 0;
        this.sold = true;
        this._save();
        // Landing a catch is what moves the price, so the market has to hear
        // about it — a glut is everyone's glut.
        this.fleet?.sold(n);
    }

    get busy() {
        return this.school !== null;
    }

    /**
     * Space, whatever space means here: break off a haul, sell a hold
     * alongside the jetty, put passengers ashore, take passengers aboard, or
     * start on a school under the bow.
     */
    toggle() {
        if (this.busy) return this.stop();
        if (this.canSell) return this.sell();
        if (this.trading?.canSell) return this.trading.sell();
        if (this.trading?.canBuy) return this.trading.buy();
        if (this.crossing?.canLand) return this.crossing.land();
        if (this.crossing?.canBoard) return this.crossing.board();

        // No fishing with passengers or cargo aboard: it is the same hold.
        if (this.crossing?.passengers > 0) return;
        if (this.trading?.count > 0) return;
        const school = this.schools.at(this.sailing.position.x, this.sailing.position.z);
        if (!school) return;

        this.school = school;
        this.partial = 0;
        // The boat lies to: no way and no steering until the nets are in.
        this.sailing.frozen = true;
    }

    stop() {
        this.school = null;
        this.partial = 0;
        this.sailing.frozen = false;
    }

    update(dt) {
        const { schools, sailing } = this;
        this.landed = false;
        this.sold = false;

        if (this.busy) {
            const s = this.school;
            // Fish come aboard at the rate that would empty this school in
            // exactly its own duration.
            this.partial += (s.size / s.duration) * dt;
            while (this.partial >= 1 && s.left > 0) {
                this.partial -= 1;
                s.left -= 1;
                this.hold += 1;
                this.landed = true;
                this._save();
                // Tell the relay the stock is one lighter. Two boats over one
                // school are drawing from the same fish, so it empties twice
                // as fast — which is the point of sharing them.
                this.fleet?.take(s.id);
            }
            if (s.left <= 0) {
                schools.remove(s);
                this.stop();
                this.hud.setHint(null);
                return;
            }
            const done = 1 - s.left / s.size;
            this.hud.setHint('fishing', done);
            return;
        }

        // Not fishing: offer whichever of the things space would do here.
        if (this.canSell) {
            this.hud.setHint('sell', 0, Math.round(this.hold * this.price));
            return;
        }
        // The goods trade speaks before the ferry does: you are alongside a
        // mill wharf or you are not, and the quays are somewhere else.
        if (this.trading?.canSell) {
            const t = this.trading;
            this.hud.setHint('unload', 0, Math.round(t.count * (t.market[t.good]?.sell ?? 0)));
            return;
        }
        if (this.trading?.canBuy) {
            const t = this.trading;
            const wharf = t.wharfAt();
            const take = t.lotHere();
            this.hud.setHint('buy', 0, take, {
                name: t.nameOf(wharf.id),
                cost: Math.round(take * (t.market[wharf.id]?.buy ?? 0)),
            });
            return;
        }
        if (this.trading?.wharfAt() && this.trading.closed && this.trading.count === 0) {
            this.hud.setHint('millshut');
            return;
        }
        if (this.crossing?.canLand) {
            this.hud.setHint('land', 0, this.crossing.passengers);
            return;
        }
        if (this.crossing?.canBoard) {
            const port = this.crossing.portAt();
            this.hud.setHint('board', 0, Math.min(
                this.crossing.queues[port.id] || 0,
                8,
            ));
            return;
        }
        // Standing at a quay after dark with people on it: say why nothing is
        // happening, rather than showing nothing at all.
        if (this.crossing?.closed && this.crossing.portAt()
            && (this.crossing.queues[this.crossing.portAt().id] || 0) > 0
            && this.crossing.passengers === 0) {
            this.hud.setHint('shut');
            return;
        }
        const over = schools.at(sailing.position.x, sailing.position.z);
        // Offering to fish with people aboard is offering something that will
        // not happen: it is one hold, and they are in it.
        if (over && this.crossing?.passengers > 0) {
            this.hud.setHint('carrying');
            return;
        }
        this.hud.setHint(over ? 'ready' : null);
    }
}
